'use client';

import type { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { AuthorCard } from '@/components/entities/author/AuthorCard';
import { TopGames } from '@/components/entities/games/TopGames/TopGames';
import { BestProviders } from '@/components/entities/providers/BestProviders';
import { ProviderBanner } from '@/components/entities/providers/ProviderBanner';
import { FaqAccordionSection } from '@/components/ui/Accordion/FaqAccordion';
import { gameQueryOptions } from '@/queries/games/games.queries';
import { providerQueryOptions } from '@/queries/providers';
import { cn } from '@/utils/cn';
import { isEmpty } from '@/utils/objects.utils';
import { getPlateContentState } from '@/utils/parsing.utils';
import { AboutGame } from './parts/AboutGame';
import { GameCompare } from './parts/GameCompare';
import { GameDetails } from './parts/GameDetails';
import { GameGallery } from './parts/GameGallery';
import { GameReview } from './parts/GameReview';
import { GameSimilarSlots } from './parts/GameSimilarSlots';
import { GameTabs } from './parts/GameTabs';
import { getAvailableGameTabs, hasVisibleGameDetails } from './utils/detailed-game.utils';

interface SeoGameViewProps {
  gameId: string;
}

export const SeoGameView: FC<SeoGameViewProps> = ({ gameId }) => {
  const { data: game } = useQuery(gameQueryOptions.one(gameId));
  const { data: similarSlots } = useQuery(gameQueryOptions.similarById(gameId));
  const providerAlias = game?.provider?.alias;
  const { data: provider } = useQuery({
    ...providerQueryOptions.byIdOrAlias(providerAlias || ''),
    enabled: !!providerAlias,
  });

  const providerForBanner = provider || game?.provider;

  const shouldShowDetails = hasVisibleGameDetails(game?.details);
  const tabs = getAvailableGameTabs(game);

  const { value: plateContentValue, hasTextContent } = getPlateContentState(game?.pageContent?.body);

  return (
    <div className="justify-start flex gap-10 w-full mb-15 md:mb-25">
      <div className="lg:max-w-221.25 3xl:max-w-242 w-full xxl:shrink-0 min-w-0">
        <h1 className="font-medium py-4 text-[1.75rem] tracking-normal leading-[1.2] capitalize text-light">
          {game?.name}
        </h1>
        {
          tabs.length > 0 && <GameTabs tabs={tabs} />
        }

        {
          !isEmpty(game?.assets) && <GameGallery assets={game!.assets!} />
        }
        <div className={cn('flex flex-col gap-13 md:gap-17', isEmpty(tabs) && 'mt-13')}>

          {
            shouldShowDetails && (
              <GameDetails details={game?.details} />
            )
          }
          {
            hasTextContent && <AboutGame contentValue={plateContentValue} />
          }

          {shouldShowDetails && <GameCompare game={game} />}

          <div
            className="xl:hidden grid grid-cols-2 gap-3"
          >
            <TopGames />
            <BestProviders />
          </div>

          {
            game?.pageContent?.faqs && game.pageContent.faqs.length > 0 && (
              <FaqAccordionSection
                className="max-w-200"
                faqsList={game.pageContent.faqs}
              />
            )
          }

          {
            providerForBanner
            && <ProviderBanner className="max-w-200" provider={providerForBanner} />
          }

          {
            game?.categories?.[0]?.id && !isEmpty(similarSlots) && (
              <GameSimilarSlots categoryId={game?.categories?.[0]?.id || ''} similarSlotsData={similarSlots} />
            )
          }

          {game?.pageContent?.author && <AuthorCard className="max-w-200" author={game.pageContent.author} />}

          <Suspense>
            <GameReview gameId={gameId} />
          </Suspense>
        </div>

      </div>
      <aside className={cn('w-full max-w-50 hidden xl:flex flex-col gap-10 sticky self-start', tabs.length === 0 ? 'mt-16.25 top-20' : 'mt-48.75 top-40')}>
        <TopGames />
        <BestProviders />
      </aside>
    </div>
  );
};
